import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input } from '@mono/ui-components';
import { ChevronLeft, MessageSquare, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody } from '../../components/layout';
import { PageHeader } from '../../components/PageHeader';
import { ChatCard } from '../../components/ProjectDashboard/ChatCard';
import { NewChatButton } from '../../components/ProjectDashboard/NewChatButton';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useCreateProjectChat, useProjectChats } from '../../hooks/useProjectChats';
export function ProjectChatList() {
    const navigate = useNavigate();
    const { projectId, project, isLoading: projectLoading, error: projectError, canEdit, isArchived } = useProjectContext();
    const [searchQuery, setSearchQuery] = useState('');
    const { data, isLoading, isError, error } = useProjectChats(projectId, {
        sort: 'createdAt',
        order: 'desc',
        search: searchQuery || undefined
    });
    const createChat = useCreateProjectChat();
    const handleCreateChat = async ()=>{
        if (!projectId) return;
        try {
            const response = await createChat.mutateAsync({
                projectId,
                data: {}
            });
            toast.success('Chat created successfully');
            navigate(`/project/${projectId}/chat/${response.chat.chatId}`);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create chat';
            toast.error(message);
        }
    };
    const handleChatClick = (chatId)=>{
        navigate(`/project/${projectId}/chat/${chatId}`);
    };
    if (projectLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-4 w-24 bg-muted rounded animate-pulse mb-2"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-32 bg-muted rounded animate-pulse"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "space-y-4",
                        "aria-busy": "true",
                        children: [
                            1,
                            2,
                            3
                        ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                className: "h-24 bg-muted rounded-lg animate-pulse"
                            }, i))
                    })
                })
            ]
        });
    }
    if (projectError || !project || !projectId) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Project not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-4",
                                children: "This project doesn't exist or you don't have access."
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: ()=>navigate('/projects'),
                                children: "Back to Projects"
                            })
                        ]
                    })
                })
            ]
        });
    }
    const chats = data?.chats ?? [];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8 pr-6 flex items-start justify-between w-full",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-xl font-semibold",
                                    children: "Chats"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Project conversations"
                                })
                            ]
                        }),
                        canEdit && !isArchived && /*#__PURE__*/ _jsx(NewChatButton, {
                            variant: "primary",
                            onSelect: handleCreateChat,
                            disabled: createChat.isPending
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative mb-6 max-w-md",
                        children: [
                            /*#__PURE__*/ _jsx(Search, {
                                className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                            }),
                            /*#__PURE__*/ _jsx(Input, {
                                type: "text",
                                placeholder: "Search chats...",
                                value: searchQuery,
                                onChange: (e)=>setSearchQuery(e.target.value),
                                className: "pl-9",
                                "aria-label": "Search chats"
                            })
                        ]
                    }),
                    isError ? /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        role: "alert",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-2",
                                children: "Failed to load chats"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: error?.message || 'Please try refreshing the page'
                            })
                        ]
                    }) : isLoading ? /*#__PURE__*/ _jsx("div", {
                        className: "space-y-4",
                        "aria-busy": "true",
                        children: [
                            1,
                            2,
                            3
                        ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                className: "h-24 bg-muted rounded-lg animate-pulse"
                            }, i))
                    }) : chats.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx(MessageSquare, {
                                className: "h-12 w-12 mx-auto mb-3 text-muted-foreground"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground mb-4",
                                children: searchQuery ? 'No chats match your search' : 'No chats yet'
                            }),
                            canEdit && !isArchived && !searchQuery && /*#__PURE__*/ _jsx(NewChatButton, {
                                variant: "primary",
                                onSelect: handleCreateChat,
                                disabled: createChat.isPending,
                                label: "Start a Chat"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: "space-y-4 max-w-2xl",
                        children: chats.map((chat)=>/*#__PURE__*/ _jsx(ChatCard, {
                                chat: chat,
                                onClick: ()=>handleChatClick(chat.chatId)
                            }, chat.chatId))
                    })
                ]
            })
        ]
    });
}
